import {
  type IBlockdaemonSDKError,
  type IGetTokensRequest,
  type IGetTokensResponse,
  isBlockdaemonSDKError,
} from "@blockdaemon/bd-sdk";
import { blockdaemonSDK } from "../../lib/sdk";
import type { TokenData } from "../hooks/use-tokens";
import { queryClient } from "../lib/query-client";

// Query key factory following the established pattern
export const tokensQueryKeys = {
  all: ["defi"] as const,
  tokens: () => [...tokensQueryKeys.all, "tokens"] as const,
  tokensList: (request?: IGetTokensRequest) =>
    [...tokensQueryKeys.tokens(), "list", request] as const,
};

// Normalize native token addresses to the 0xeeee... sentinel used across the app
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
const EEEE_ADDRESS = "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee";

export async function fetchSdkTokens(request?: IGetTokensRequest) {
  try {
    const response: IGetTokensResponse | IBlockdaemonSDKError =
      await blockdaemonSDK.common.getAssets(request ?? {});

    if (isBlockdaemonSDKError(response)) {
      throw new Error(response.detail || "Failed to fetch tokens");
    }

    const data = (response as IGetTokensResponse).data;
    // Replace zero-address with 0xeeee... for native assets so downstream consumers
    // don't need to special-case native tokens per feature.
    const normalized = data.map((t) => ({
      ...t,
      address: t.address?.toLowerCase() === ZERO_ADDRESS ? EEEE_ADDRESS : t.address,
    }));
    return normalized as TokenData[];
  } catch (error) {
    throw error instanceof Error ? error : new Error("Failed to fetch tokens");
  }
}

export async function prefetchSdkTokens(request?: IGetTokensRequest): Promise<void> {
  queryClient.prefetchQuery({
    queryKey: tokensQueryKeys.tokensList(request),
    queryFn: () => fetchSdkTokens(request),
    staleTime: 5 * 60 * 1000, // 5 minutes cache for token data
  });
}
